<template>
  <div id="login-page">
    <b-card class="login-form">
      <template #header>
        <h2 class="mb-0">Merchant Login</h2>
      </template>
      <b-form class="text-center" @submit.prevent="handleSubmit">
        <b-form-group>
          <b-form-input
            v-model="email"
            placeholder="Email"
            data-cy="email"
            trim
            lazy
          />
        </b-form-group>

        <b-form-group>
          <b-form-input
            v-model="password"
            type="password"
            placeholder="Password"
            data-cy="password"
          />
        </b-form-group>

        <b-button
          pill
          variant="outline-blue"
          type="submit"
          data-cy="login"
          data-testid="login-submit-btn"
          :disabled="isLoading"
        >
          <span v-if="!isLoading">Login</span>
          <b-spinner v-else small />
        </b-button>

        <b-alert
          :show="errorMessages.length > 0"
          variant="danger"
          class="errors text-left"
          data-cy="login-errors"
        >
          <div
            v-for="(error, index) in errorMessages"
            :key="`login-error-${index}`"
            class="error"
          >
            {{ error }}
          </div>
        </b-alert>

        <div class="login-help">
          <router-link :to="{ name: 'ForgotPassword' }">
            Trouble logging in?
          </router-link>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import store from '@/store/index'
import { toErrorList } from '@/utils/apiErrors'
import { loadDefaultMerchant } from '@/utils/startup-load.js'
export default {
  beforeRouteEnter: async (to, from, next) => {
    if ((await store.getters.isAuthenticated) && !to.meta.isSSO) {
      next({
        name: 'Home'
      })
    } else {
      next()
    }
  },
  data() {
    return {
      email: '',
      password: '',
      // Display-ready strings only. Anything shaped like an API body is
      // flattened by toErrorList before it lands here.
      errorMessages: [],
      isLoading: false
    }
  },

  computed: {
    isSSO() {
      return this.$route &&
        this.$route.query &&
        this.$route.query.id &&
        this.$route.query.token
        ? true
        : false
    }
  },
  created() {
    if (this.isSSO) {
      if (this.$route.query.account_id) {
        this.$store.commit('setCurrentAccount', {
          id: this.$route.query.account_id
        })
      }
      this.handleSubmit()
    }
  },
  methods: {
    async handleSubmit() {
      this.errorMessages = []
      const credentials = this.isSSO
        ? {
            id: this.$route.query?.id || '',
            token: this.$route.query?.token || ''
          }
        : { email: this.email, password: this.password }

      this.isLoading = true

      try {
        await this.$store.dispatch('login', credentials)
      } catch (error) {
        this.isLoading = false
        if (error?.response?.status === 401) {
          this.errorMessages = [
            'No active account found with the given credentials'
          ]

          return
        }

        // Always surface exactly one readable failure. A body we cannot parse
        // (an HTML 500 page, a gateway fault) becomes the generic message
        // rather than being flattened character-by-character onto the screen.
        this.errorMessages = toErrorList(
          error?.response?.data ?? error?.normalized?.message
        )
        console.error('Login failed', error)

        return
      }

      // The credentials were good. Whatever fails from here, including a 401
      // from the merchant load, says nothing about the password.
      try {
        await this.$store.dispatch('getProfile')

        if (this.$store.getters.accessClaims.roles.length === 0) {
          this.$router.push({ name: 'UnAuthorized' })
        } else {
          await loadDefaultMerchant(this.$store)
          this.$store.commit('setStartupLoadError', null)

          this.$nextTick(() => {
            this.isLoading = false
            this.$router.push(
              this.$route.query?.next || {
                name: 'Home'
              }
            )
          })
        }
      } catch (error) {
        this.isLoading = false
        this.errorMessages = ['Sign-in did not finish. Please try again.']
        // The session is signed in with no merchant: an in-app move off this page shows the
        // recovery screen rather than a blank one.
        this.$store.commit('setStartupLoadError', {
          status: error?.response?.status ?? null
        })
        console.error('Sign-in did not finish', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#login-page {
  display: flex;
  align-items: center;
  height: 100vh;
  .login-form {
    width: 500px;
    margin: 0 auto;
    @media (max-width: 500px) {
      width: 90%;
      min-width: 350px;
    }
  }

  // The alert is absent when there is no error, so it cannot be what separates
  // the submit button from the link below it — each owns its own top margin.
  .errors {
    margin: 20px 0 0;
  }

  .login-help {
    margin-top: 20px;
  }

  .error {
    // A server message can be long or unbroken; wrap it instead of letting it
    // widen the card.
    overflow-wrap: break-word;

    &::first-letter {
      text-transform: capitalize;
    }
  }
}
</style>
